import { API_BASE_URL } from "../config";
import { auth } from "../config";
import { TaskStatus } from "../constants/taskStatus";

const ApiEndpoints = {
  SUBMIT: "/draw",
  STATUS: (taskId) => `/draw/${taskId}/status`,
};

const ImageConfig = {
  FORMAT: "png",
  MIME_TYPE: "image/png",
  SCALE: 0.5,
  MAX_RESOLUTION: 1024 * 1024,
  MIN_RESOLUTION: 768 * 768,
};

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 90; // 3 minutes

/**
 * Resizes an image blob to fit within specified resolution constraints
 */
async function resizeImageBlob(imageBlob, maxResolution, minResolution) {
  const img = new Image();
  img.src = URL.createObjectURL(imageBlob);

  await new Promise((resolve) => {
    img.onload = () => resolve();
  });

  const currentResolution = img.width * img.height;

  if (currentResolution <= maxResolution && currentResolution >= minResolution) {
    URL.revokeObjectURL(img.src);
    return imageBlob;
  }

  let scaleFactor;
  if (currentResolution > maxResolution) {
    scaleFactor = Math.sqrt(maxResolution / currentResolution);
  } else {
    scaleFactor = Math.sqrt(minResolution / currentResolution);
  }

  const newWidth = Math.floor(img.width * scaleFactor);
  const newHeight = Math.floor(img.height * scaleFactor);

  const canvas = document.createElement("canvas");
  canvas.width = newWidth;
  canvas.height = newHeight;

  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, newWidth, newHeight);

  URL.revokeObjectURL(img.src);

  return new Promise((resolve) => {
    canvas.toBlob(resolve, ImageConfig.MIME_TYPE);
  });
}

/**
 * Prepare image blob from editor selection
 */
export async function prepareImageBlob(editor) {
  const selectedShapes = editor.getSelectedShapes();
  if (selectedShapes.length === 0) {
    throw new Error("No shapes selected");
  }

  const selectedShapeIds = selectedShapes.map((shape) => shape.id);

  const { blob } = await editor.toImage(selectedShapeIds, {
    format: ImageConfig.FORMAT,
    scale: ImageConfig.SCALE,
    background: true,
  });

  return resizeImageBlob(
    blob,
    ImageConfig.MAX_RESOLUTION,
    ImageConfig.MIN_RESOLUTION
  );
}

/**
 * Submit generation to backend. Returns { id } task ID.
 */
export async function submitGeneration(blob, prompt, style, modelTier) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("Must be signed in to generate");
  }
  const idToken = await user.getIdToken();

  const formData = new FormData();
  formData.append("file", blob, `drawing.${ImageConfig.FORMAT}`);
  if (prompt) {
    formData.append("prompt", prompt);
  }
  if (style) {
    formData.append("style", style);
  }
  formData.append("model_tier", modelTier || "quality");

  const response = await fetch(`${API_BASE_URL}${ApiEndpoints.SUBMIT}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || "Failed to submit generation");
  }

  return response.json();
}

/**
 * Poll for generation result. Calls onComplete/onError when terminal state reached.
 * Returns an interval ID that can be cleared to stop polling.
 */
export function pollForResult(taskId, { onComplete, onError }) {
  let attempts = 0;
  let resolved = false;
  let intervalId = null;

  const stop = () => {
    resolved = true;
    if (intervalId) clearInterval(intervalId);
  };

  const poll = async () => {
    if (resolved) return;

    try {
      const user = auth.currentUser;
      if (!user) {
        stop();
        onError(new Error("User signed out"));
        return;
      }

      const idToken = await user.getIdToken();
      const response = await fetch(
        `${API_BASE_URL}${ApiEndpoints.STATUS(taskId)}`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );

      if (resolved) return;

      if (!response.ok) {
        stop();
        onError(new Error("Failed to check generation status"));
        return;
      }

      const data = await response.json();

      if (resolved) return;

      if (data.status === TaskStatus.COMPLETE) {
        stop();
        onComplete(data.results);
      } else if (
        data.status === TaskStatus.ERROR ||
        data.status === TaskStatus.FAILED
      ) {
        stop();
        onError(new Error("Generation failed"));
      }

      attempts++;
      if (attempts >= MAX_POLL_ATTEMPTS) {
        stop();
        onError(new Error("Generation timed out"));
      }
    } catch (err) {
      stop();
      onError(err);
    }
  };

  // Poll immediately, then on interval
  poll();
  intervalId = setInterval(poll, POLL_INTERVAL_MS);

  return intervalId;
}
